import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { useDemo } from "../context/DemoContext";
import { PRODUCTS, SERVICES, CUSTOMERS, APPROACH_STEPS, IMAGES } from "../data/content";
import { ARTICLES } from "../data/articles";

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 50]);
  const imgYSlow = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 24]);
  const imgYFast = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 84]);
  const { openDemo } = useDemo();

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-20" data-testid="home-hero">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[36rem] bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
      />
      <div className="container-x relative grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.span
            className="eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            ZasyaOnline Pvt Ltd · Product-led technology company
          </motion.span>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            <MaskedLines
              lines={[
                "Software products,",
                "built by the team",
                <>you can also <span className="brand-gradient-text">hire</span>.</>,
              ]}
            />
          </h1>
          <motion.p
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.55 }}
          >
            ZasyaOnline builds its own products — MoveOS for fleets, myBantu for AI
            call centres, LearnSphere for enterprise learning and CraneSoftwares for
            crane rental — and brings the same engineering to consulting, integration
            and managed services for your business.
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.7 }}
          >
            <Link to="/products" className="btn-primary" data-testid="hero-explore-products">
              Explore the products <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <button type="button" onClick={() => openDemo()} className="btn-secondary" data-testid="hero-book-demo">
              Book a Demo
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            className="relative pb-16 pr-4 sm:pb-20"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            data-testid="hero-montage"
          >
            {/* Main frame: MoveOS dashboard */}
            <motion.div style={{ y: imgY }} className="relative">
              <Link
                to="/products/moveos"
                className="clip-frame group block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
                data-testid="hero-montage-moveos"
              >
                <img
                  src="/assets/products/moveos-screen-dashboard.png"
                  alt="MoveOS executive dashboard — real product screen with sensitive details masked"
                  className="h-64 w-full object-cover object-left-top sm:h-80"
                  loading="eager"
                />
              </Link>
            </motion.div>

            {/* Floating: LearnSphere */}
            <motion.div
              className="absolute -top-8 -right-2 w-44 sm:-right-6 sm:w-56"
              style={{ y: reduce ? 0 : imgYSlow }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.75 }}
            >
              <Link
                to="/products/learnsphere"
                className="block rotate-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 transition-[transform] duration-300 hover:rotate-0"
                data-testid="hero-montage-learnsphere"
              >
                <img
                  src="/assets/products/learnsphere-screen-dashboard.png"
                  alt="LearnSphere admin dashboard — real product screen with sensitive details masked"
                  className="h-24 w-full object-cover object-right-top sm:h-32"
                  loading="eager"
                />
              </Link>
            </motion.div>

            {/* Floating: CraneSoftwares */}
            <motion.div
              className="absolute -bottom-2 -left-3 w-48 sm:-left-8 sm:w-64"
              style={{ y: reduce ? 0 : imgYFast }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <Link
                to="/products/cranesoftwares"
                className="block -rotate-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 transition-[transform] duration-300 hover:rotate-0"
                data-testid="hero-montage-cranesoftwares"
              >
                <img
                  src="/assets/products/cranesoftwares-screen-reports.png"
                  alt="CraneSoftwares executive reports — real product screen with sensitive details masked"
                  className="h-24 w-full object-cover object-left-top sm:h-32"
                  loading="eager"
                />
              </Link>
            </motion.div>

            {/* Floating: myBantu phone */}
            <motion.div
              className="absolute -bottom-10 right-6 w-20 sm:right-10 sm:w-24"
              style={{ y: reduce ? 0 : imgYSlow }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05 }}
            >
              <Link
                to="/products/mybantu"
                className="block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 transition-[transform] duration-300 hover:-translate-y-1"
                data-testid="hero-montage-mybantu"
              >
                <img
                  src="/assets/products/mybantu-app-home.png"
                  alt="myBantu mobile app home — real product screen with sensitive details masked"
                  className="h-36 w-full object-cover object-top sm:h-44"
                  loading="eager"
                />
              </Link>
            </motion.div>

            <motion.span
              className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-slate-200 bg-white/90 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              Real product screens
            </motion.span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Portfolio = () => (
  <section className="py-20 sm:py-28" data-testid="home-portfolio">
    <div className="container-x">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">The Product Portfolio</span>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Four products. Four industries. One engineering standard.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Each product is built, run and supported by ZasyaOnline — and each one
              deepens the expertise we bring to consulting engagements.
            </p>
          </div>
          <Link to="/products" className="btn-secondary" data-testid="portfolio-view-all">
            Products overview <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PRODUCTS.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const ConsultingTeaser = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="home-consulting">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <span className="eyebrow">Consulting &amp; Solutions</span>
        <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Beyond our products: technology capability for hire.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          The team that builds our products also designs, integrates and runs
          technology for enterprises — from custom applications to contact-centre
          implementations.
        </p>
        <Link to="/consulting" className="btn-dark mt-8" data-testid="consulting-teaser-link">
          Explore services <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </Reveal>
      <div className="grid gap-3 sm:grid-cols-2 lg:col-span-7">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06}>
            <div className="card-line h-full p-5 transition-colors duration-200 hover:border-teal-600/50">
              <h3 className="font-display text-sm font-bold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{s.need}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CustomersStrip = () => (
  <section className="py-20 sm:py-28" data-testid="home-customers">
    <div className="container-x">
      <Reveal className="max-w-2xl">
        <span className="eyebrow">Customers &amp; Engagements</span>
        <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Trusted in production, across industries.
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {CUSTOMERS.slice(0, 4).map((c, i) => (
          <Reveal key={c.name} delay={i * 0.06}>
            <div className="card-line h-full p-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">
                {c.engagement}
              </span>
              <h3 className="mt-2 font-display text-base font-bold text-slate-900">{c.name}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{c.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link to="/customers" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800" data-testid="customers-view-all">
          See all engagements <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ApproachStrip = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="home-approach">
    <div className="container-x grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <div className="clip-frame overflow-hidden rounded-3xl border border-slate-200">
          <img
            src={IMAGES.teamMeeting}
            alt="ZasyaOnline team collaborating in a design session"
            className="h-72 w-full object-cover sm:h-96"
            loading="lazy"
          />
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <Reveal>
          <span className="eyebrow">How we work</span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Discovery to improvement, without the theatre.
          </h2>
        </Reveal>
        <div className="mt-8 space-y-5">
          {APPROACH_STEPS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.06}>
              <div className="flex gap-4">
                <span className="font-mono text-sm font-medium text-blue-700">{s.step}</span>
                <div>
                  <h3 className="font-display text-base font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.detail}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <Link to="/about" className="btn-secondary mt-8" data-testid="approach-about-link">
            About ZasyaOnline <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const InsightsTeaser = () => (
  <section className="py-20 sm:py-28" data-testid="home-insights">
    <div className="container-x">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="eyebrow">Insights</span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Notes from the industries we build for.
          </h2>
        </div>
        <Link to="/insights" className="btn-secondary" data-testid="insights-view-all">
          All insights <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
      </Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {ARTICLES.slice(0, 3).map((a, i) => (
          <Reveal key={a.slug} delay={i * 0.08} className="h-full">
            <Link
              to={`/insights/${a.slug}`}
              className="group card-line flex h-full flex-col overflow-hidden transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-500/60 hover:shadow-xl hover:shadow-blue-600/10"
              data-testid={`insight-card-${a.slug}`}
            >
              <div className="h-44 overflow-hidden">
                <img
                  src={a.image}
                  alt={a.imageAlt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">{a.category}</span>
                <h3 className="mt-2 font-display text-lg font-bold leading-snug tracking-tight text-slate-900">
                  {a.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                <span className="mt-4 text-xs text-slate-400">
                  {new Date(a.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <SEO path="/" />
      <Hero />
      <Marquee />
      <Portfolio />
      <ConsultingTeaser />
      <CustomersStrip />
      <ApproachStrip />
      <InsightsTeaser />
      <CTASection />
    </>
  );
}
